import { formatText, type Label, type Locale } from "../texts";
import type { ServiceTone } from "../ui/pieces";
import { plural } from "./organizations";

// R5 · Empleados IA y conocimiento: tipos de lo que devuelve el BFF y las palabras de cada estado (una sola definición
// para lista, ficha y ventanas, E52). Los códigos internos (de empleado, acción, paquete o término) nunca se muestran (D7).

export type OrgHead = { id: number; name: string; code: string; timezone: string };

export type EmployeeRow = {
  id: number;
  name: string;
  job: string | null;
  /** AR11 · código del trabajo: la columna dice el texto del diccionario, el mismo de la píldora. */
  jobCode?: string | null;
  active: boolean;
  available: boolean;
};

export type Employee = EmployeeRow & {
  jobCode: string | null;
  objective: string | null;
  instructions: string | null;
  communicationStyle: string | null;
};

/** Trabajos que se eligen al crear o editar (Empleado.razor), con su clave de texto. */
export const jobRoles = [
  { code: "VENTAS", key: "ADM_EMP_JOB_SALES" },
  { code: "ATENCION", key: "ADM_EMP_JOB_SUPPORT" },
  { code: "COBRANZAS", key: "ADM_EMP_JOB_COLLECTIONS" },
  { code: "OTRO", key: "ADM_EMP_JOB_OTHER" },
] as const;

/** Límites de hoy (EmployeesIA): nombre 2–150, objetivo 2000, instrucciones 8000, estilo 500. */
export const employeeLimits = { name: 150, objective: 2000, instructions: 8000, style: 500 } as const;

/** Trabajo del empleado: la palabra del trabajo elegido o, si es otro, el nombre que guarda el servicio. */
export function jobText(L: Label, employee: Pick<Employee, "job"> & { jobCode?: string | null }): string {
  const known = jobRoles.find((role) => role.code === employee.jobCode);
  return known ? L(known.key) : (employee.job ?? "—");
}

export const statusText = (L: Label, active: boolean) => L(active ? "ADM_EMP_ACTIVE" : "ADM_EMP_INACTIVE");

export const availabilityText = (L: Label, available: boolean) =>
  L(available ? "ADM_EMP_AVAILABLE" : "ADM_EMP_UNAVAILABLE");

export function employeeHref(orgId: number, employeeId: number, tab: EmployeeTab = "datos") {
  return `/Organizations/Empleado?id=${orgId}&employeeId=${employeeId}${tab === "datos" ? "&tab=datos" : `&tab=${tab}`}`;
}

export const employeeTabs = ["datos", "capacidades", "conocimiento"] as const;
export type EmployeeTab = (typeof employeeTabs)[number];

export const normalizeEmployeeTab = (value: string | null): EmployeeTab =>
  (employeeTabs as readonly string[]).includes(value ?? "") ? (value as EmployeeTab) : "datos";

// ---- Capacidades ----

export type Pack = { code: string; name: string; access: "READ" | "WRITE"; actions: number };
export type Relation = {
  parentId: number;
  childId: number;
  otherId: number;
  name: string | null;
  job: string | null;
  kind: "supervises" | "supervised" | "peer" | "created" | "createdBy";
};
export type Tool = {
  code: string;
  name: string;
  changesData: boolean;
  risk: "low" | "medium" | "high";
  state: "granted" | "blocked" | "needsWrite" | "notGranted";
  allowed: boolean;
  approval: string;
};
export type Capabilities = {
  organization: OrgHead;
  employee: Employee;
  packs: Pack[];
  available: { code: string; name: string; actions: number }[];
  relations: Relation[] | null;
  candidates: { id: number; name: string; job: string | null }[];
  tools: Tool[];
};

/** Política de aprobación de una acción, con las palabras de la especificación (D4). */
export function policyText(L: Label, policy: string | null | undefined): string {
  if (policy === "APPROVE_ALWAYS") return L("ADM_APR_POLICY_ALWAYS");
  if (policy === "CONFIRM_IF_INFERRED") return L("ADM_APR_POLICY_INFERRED");
  return L("ADM_APR_POLICY_NONE");
}

/** Orden de exigencia: solo se puede endurecer (NONE < CONFIRM_IF_INFERRED < APPROVE_ALWAYS). */
export const policyRank = (policy: string) =>
  policy === "APPROVE_ALWAYS" ? 2 : policy === "CONFIRM_IF_INFERRED" ? 1 : 0;

export const riskKey = { low: "ADM_EMP_RISK_LOW", medium: "ADM_EMP_RISK_MEDIUM", high: "ADM_EMP_RISK_HIGH" } as const;

/** C55 · estado de una acción para este empleado (Concedida · Necesita escritura · Bloqueada · Sin paquete). */
export function toolState(tool: Pick<Tool, "state">): { tone: ServiceTone; key: string } {
  switch (tool.state) {
    case "granted":
      return { tone: "ok", key: "ADM_EMP_TOOL_GRANTED" };
    case "blocked":
      return { tone: "fail", key: "ADM_EMP_TOOL_BLOCKED" };
    case "needsWrite":
      return { tone: "warn", key: "ADM_EMP_TOOL_NEEDS_WRITE" };
    default:
      return { tone: "off", key: "ADM_EMP_TOOL_NOT_GRANTED" };
  }
}

/** Frase de una relación vista desde el empleado abierto («Valeria lo supervisa», «trabajan como pares»). */
export function relationLine(L: Label, relation: Relation, employeeName: string): string {
  switch (relation.kind) {
    case "supervises":
      return formatText(L("ADM_EMP_REL_SUPERVISES_LINE"), employeeName);
    case "supervised":
      return L("ADM_EMP_REL_SUPERVISED_LINE");
    case "created":
      return formatText(L("ADM_EMP_REL_CREATED_LINE"), employeeName);
    case "createdBy":
      return L("ADM_EMP_REL_CREATED_BY_LINE");
    default:
      return L("ADM_EMP_REL_PEER_LINE");
  }
}

/** «Supervisa a 1 empleado IA · nadie la supervisa · 1 par», del mismo conjunto que la lista (E52). */
export function relationSummary(L: Label, locale: Locale, relations: Relation[]): string {
  const supervises = relations.filter((item) => item.kind === "supervises").length;
  const supervisors = relations.filter((item) => item.kind === "supervised").length;
  const peers = relations.filter((item) => item.kind === "peer").length;
  return [
    supervises
      ? formatText(L("ADM_EMP_REL_SUM_SUPERVISES"), plural(L, locale, supervises, "ADM_EMP_N_ONE", "ADM_EMP_N"))
      : L("ADM_EMP_REL_SUM_SUPERVISES_NONE"),
    supervisors
      ? formatText(L("ADM_EMP_REL_SUM_SUPERVISED"), plural(L, locale, supervisors, "ADM_EMP_N_ONE", "ADM_EMP_N"))
      : L("ADM_EMP_REL_SUM_SUPERVISED_NONE"),
    plural(L, locale, peers, "ADM_EMP_REL_SUM_PEER_ONE", "ADM_EMP_REL_SUM_PEER"),
  ].join(" · ");
}

// ---- Conocimiento ----

export type Term = { id: number; kind: "AREA" | "PROCESS" | "TOPIC"; name: string; active: boolean };

export const audiences = ["PUBLIC", "CUSTOMER", "INTERNAL", "RESTRICTED"] as const;
export const audienceKey: Record<string, string> = {
  PUBLIC: "ADM_KB_AUD_PUBLIC",
  CUSTOMER: "ADM_KB_AUD_CUSTOMER",
  INTERNAL: "ADM_KB_AUD_INTERNAL",
  RESTRICTED: "ADM_KB_AUD_RESTRICTED",
};

export const termKindKey: Record<string, string> = {
  AREA: "ADM_KB_TERM_AREA",
  PROCESS: "ADM_KB_TERM_PROCESS",
  TOPIC: "ADM_KB_TERM_TOPIC",
};

/** Roles de la empresa que la clasificación ofrece (los demás que ya tuviera se conservan al guardar). */
export const orgRoles = ["ORG_MEMBER", "ORG_ADMIN", "ORG_OWNER"] as const;

export const sourceTypeKey: Record<string, string> = {
  TEXT: "ADM_KB_TYPE_TEXT",
  URL: "ADM_KB_TYPE_URL",
  FILE: "ADM_KB_TYPE_FILE",
};

/**
 * Versión de una fuente. `processing` es la preparación que decide el servidor (RC2): «Indexada» solo cuando terminó de
 * procesarse y su índice quedó listo; «Falló» si falló el procesamiento o el índice; si no, «En proceso».
 */
export type Version = {
  id: number;
  number: number;
  processing: "RUNNING" | "INDEXED" | "FAILED";
  /** Se puede reintentar su procesamiento. */
  retryable: boolean;
  /** Paso en curso o el que falló (lo decide Knowledge): el procesamiento del contenido o el índice. */
  step?: "processing" | "index" | null;
  /** Se puede «Reintentar índice»: se procesó y su índice falló (lo decide Knowledge). */
  indexRetryable?: boolean;
  /** Motivo en palabras de un índice que falló (clave de texto; nunca el código). */
  failureKey?: string | null;
  approval: "PENDING" | "APPROVED" | "REJECTED";
  createdAt: string | null;
  current: boolean;
};

export const processingKey: Record<string, string> = {
  RUNNING: "ADM_KB_PROC_RUNNING",
  INDEXED: "ADM_KB_PROC_READY",
  FAILED: "ADM_KB_PROC_FAILED",
};

/** Texto de una versión que se prepara: «Indexando…» si ya se procesó y falta su índice; si no, «En proceso». */
export const runningKey = (version: Version) =>
  version.step === "index" ? "ADM_KB_PROC_INDEXING" : "ADM_KB_PROC_RUNNING";

/** D15 · mientras una versión se procesa o se indexa, la tabla se actualiza sola; al terminar, deja de hacerlo. */
export const isProcessing = (versions: Version[] | null | undefined) =>
  !!versions?.some((item) => item.processing === "RUNNING");

/** Lo que se puede hacer con una versión: aprobar o rechazar una indexada pendiente, reintentar una que falló. */
export const canDecideVersion = (version: Version) =>
  version.processing === "INDEXED" && version.approval === "PENDING";
export const canRetryVersion = (version: Version) => version.processing === "FAILED" && version.retryable;
export const canRetryIndex = (version: Version) => version.processing === "FAILED" && !!version.indexRetryable;

export type Classification = {
  audience: string | null;
  terms: number[];
  roles: string[];
  permissions: string[];
  options: Term[];
};

// ---- Aprobaciones ----

export const approvalStatuses = ["PENDING", "APPROVED", "DENIED", "EXPIRED", "ALL"] as const;

export const approvalPillKey: Record<string, string> = {
  PENDING: "ADM_APR_PILL_PENDING",
  APPROVED: "ADM_APR_PILL_APPROVED",
  DENIED: "ADM_APR_PILL_DENIED",
  EXPIRED: "ADM_APR_PILL_EXPIRED",
  ALL: "ADM_APR_PILL_ALL",
};

/** Estado de una solicitud: pendiente en ocre; las demás en texto (C15). */
export const approvalStatusKey: Record<string, string> = {
  PENDING: "ADM_APR_STATUS_PENDING",
  APPROVED: "ADM_APR_STATUS_APPROVED",
  DENIED: "ADM_APR_STATUS_DENIED",
  EXPIRED: "ADM_APR_STATUS_EXPIRED",
};

/** Contador de la cola con el mismo total que el paginado (E100): «7 pendientes», «12 solicitudes». */
export function approvalsCounter(L: Label, locale: Locale, status: string, total: number): string {
  const keys: Record<string, [string, string]> = {
    PENDING: ["ADM_APR_N_PENDING_ONE", "ADM_APR_N_PENDING"],
    APPROVED: ["ADM_APR_N_APPROVED_ONE", "ADM_APR_N_APPROVED"],
    DENIED: ["ADM_APR_N_DENIED_ONE", "ADM_APR_N_DENIED"],
    EXPIRED: ["ADM_APR_N_EXPIRED_ONE", "ADM_APR_N_EXPIRED"],
  };
  const [one, many] = keys[status] ?? ["ADM_APR_N_ALL_ONE", "ADM_APR_N_ALL"];
  return plural(L, locale, total, one, many);
}

export const intentKey = (intent: string) =>
  intent === "EXPLICIT_REQUEST" ? "ADM_APR_INTENT_EXPLICIT" : "ADM_APR_INTENT_INFERRED";

/**
 * Nombre de un argumento con la palabra del negocio (ADM_APR_ARG_*); sin clave, el nombre con espacios y mayúscula
 * («to_agent» → «To agent») en vez del código crudo.
 */
export function argumentLabel(L: Label, key: string): string {
  const textKey = `ADM_APR_ARG_${key.replace(/[^A-Za-z0-9]+/g, "_").toUpperCase()}`;
  const text = L(textKey);
  if (!text.startsWith("⟦")) return text;
  const words = key
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[._-]+/g, " ")
    .trim()
    .toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : key;
}

/** Un permiso del catálogo de Identity (B10): el código se guarda; se muestra su nombre. */
export type PermissionEntry = { code: string; name: string };

/**
 * Nombre legible de un permiso en el idioma de la pantalla: el texto del Admin (`ADM_PERM_*`) o, si no lo hay, el
 * nombre que guarda Identity. Un código guardado que ya no está en el catálogo dice «Permiso retirado» (D7: sin código).
 */
export function permissionName(L: Label, code: string, catalog: PermissionEntry[]): string {
  const text = L(`ADM_PERM_${code.toUpperCase().replace(/[^A-Z0-9]+/g, "_")}`);
  if (!text.startsWith("⟦")) return text;
  return catalog.find((item) => item.code === code)?.name ?? L("ADM_KB_PERM_RETIRED");
}

/**
 * Opciones del selector: los guardados que el catálogo no ofrece primero (para poder quitarlos: retirados o, desde RC1 ·
 * DF2, de plataforma, que una persona de la empresa no puede tener) y el catálogo por nombre. Guardar los conserva (E105).
 */
export function permissionOptions(L: Label, chosen: string[], catalog: PermissionEntry[]) {
  const known = new Set(catalog.map((item) => item.code));
  return [
    ...chosen
      .filter((code) => !known.has(code))
      .map((code) => ({ value: code, label: permissionName(L, code, catalog) })),
    ...catalog
      .map((item) => ({ value: item.code, label: permissionName(L, item.code, catalog) }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];
}

/** Lo que dice el botón: «Ninguno», el nombre del elegido o «{primero} y N más». */
export function permissionsSummary(L: Label, chosen: string[], catalog: PermissionEntry[]): string {
  if (chosen.length === 0) return L("ADM_KB_NONE");
  const first = permissionName(L, chosen[0]!, catalog);
  return chosen.length === 1 ? first : formatText(L("ADM_KB_PERMS_MORE"), first, chosen.length - 1);
}
